import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CollapsibleSectionComponent } from '@brightnav/ui';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { Button } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { CreditOverviewStore } from '../credit-overview.store';
import type {
  CreditDebtAggregationType,
  CreditReportAccount,
  CreditScoreHistoryPoint,
} from '../credit-overview.model';
import { CreditOverviewStateBadgeComponent } from './credit-overview-state-badge.component';
import {
  CreditReportDialogComponent,
  type CreditReportDialogData,
} from '../report/credit-report-dialog.component';
import { CreditOverviewSendAction } from '../send/send-to-client.action';

type LiabilitySortColumn = keyof Pick<
  CreditReportAccount,
  | 'lender'
  | 'type'
  | 'balance'
  | 'creditLimit'
  | 'utilizationRate'
  | 'minPayment'
  | 'originDate'
  | 'status'
>;

type SortDirection = 'asc' | 'desc';

const LIABILITY_SORT_COLUMNS: LiabilitySortColumn[] = [
  'lender',
  'type',
  'balance',
  'creditLimit',
  'utilizationRate',
  'minPayment',
  'originDate',
  'status',
];

const AGGREGATION_LABELS: Record<CreditDebtAggregationType, string> = {
  CREDIT_CARD: 'Credit card',
  FLEXIBLE_CREDIT_CARD: 'Flexible credit card',
  SECURED_CREDIT_CARD: 'Secured credit card',
  AUTO: 'Auto',
  EDUCATION_LOAN: 'Education loan',
  SECURED: 'Secured',
  UNSECURED: 'Unsecured',
};

@Component({
  selector: 'bn-credit-overview-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button,
    CollapsibleSectionComponent,
    CreditOverviewStateBadgeComponent,
    CurrencyPipe,
    NgTemplateOutlet,
    ScrollRegionComponent,
    TableModule,
  ],
  providers: [CreditOverviewStore, DialogService],
  templateUrl: './credit-overview-card.component.html',
  styleUrl: './credit-overview-card.component.scss',
})
export class CreditOverviewCardComponent {
  readonly store = inject(CreditOverviewStore);
  readonly sendAction = inject(CreditOverviewSendAction);
  private readonly _dialog = inject(DialogService);
  private readonly _destroyRef = inject(DestroyRef);
  private _reportRef: DynamicDialogRef | null = null;
  private _reportGuid: string | null = null;
  readonly pageSize = 5;
  readonly page = signal(0);
  readonly sortColumn = signal<LiabilitySortColumn>('originDate');
  readonly sortDirection = signal<SortDirection>('desc');
  readonly sortOrder = computed(() =>
    this.sortDirection() === 'asc' ? 1 : -1,
  );
  readonly hasUsableScore = computed(() => {
    const score = this.store.summary()?.creditScore?.score;
    return typeof score === 'number' && score !== 4;
  });
  readonly reportAvailable = computed(
    () => this.store.summary()?.reportAvailable !== false,
  );
  readonly scoreHistory = computed(() =>
    [...(this.store.summary()?.creditScore?.scoreHistory ?? [])]
      .filter(
        (
          point,
        ): point is CreditScoreHistoryPoint & { score: number; date: string } =>
          typeof point.score === 'number' && typeof point.date === 'string',
      )
      .sort((a, b) => a.date.localeCompare(b.date)),
  );
  readonly debtAggregation = computed(() =>
    (this.store.summary()?.debt.summary.aggregation ?? []).map((item) => ({
      ...item,
      label: AGGREGATION_LABELS[item.type],
      percentage: this._parsePercentage(item.percentage),
    })),
  );
  readonly debtAggregationLabel = computed(() =>
    this.debtAggregation()
      .map((item) => `${item.label} ${item.percentage}%`)
      .join(', '),
  );
  readonly firstHistoryLabel = computed(() =>
    this._formatMonthYear(this.scoreHistory()[0]?.date),
  );
  readonly lastHistoryLabel = computed(() =>
    this._formatMonthYear(this.scoreHistory().at(-1)?.date),
  );
  readonly momentumPoints = computed(() => {
    const history = this.scoreHistory();
    if (history.length < 2) return '';
    const width = 900;
    const height = 120;
    const { minimum, maximum } = this._scoreDomain(
      history.map(({ score }) => score),
    );
    return history
      .map((point, index) => {
        const x = (index / (history.length - 1)) * width;
        const y =
          height - ((point.score - minimum) / (maximum - minimum)) * height;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  });
  readonly momentumAxisLabels = computed(() => {
    const history = this.scoreHistory();
    if (history.length < 2) return [];
    const { minimum, maximum } = this._scoreDomain(
      history.map(({ score }) => score),
    );
    return [maximum, Math.round((maximum + minimum) / 2), minimum];
  });
  readonly momentumAccessibleLabel = computed(
    () =>
      `Credit score trend: ${this.scoreHistory()
        .map((point) => `${this._formatMonthYear(point.date)} ${point.score}`)
        .join(', ')}`,
  );
  readonly sortedAccounts = computed(() => {
    const column = this.sortColumn();
    const direction = this.sortDirection() === 'asc' ? 1 : -1;
    return [...(this.store.summary()?.debt.accounts ?? [])].sort((a, b) => {
      const aValue = a[column];
      const bValue = b[column];
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      const comparison =
        typeof aValue === 'number' && typeof bValue === 'number'
          ? aValue - bValue
          : String(aValue).localeCompare(String(bValue), undefined, {
              numeric: true,
              sensitivity: 'base',
            });
      return comparison * direction;
    });
  });
  readonly totalDebt = computed(() =>
    Number(this.store.summary()?.debt.summary.totalDebt ?? 0),
  );
  readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.sortedAccounts().length / this.pageSize)),
  );
  readonly visibleAccounts = computed(() => {
    const start = this.page() * this.pageSize;
    return this.sortedAccounts().slice(start, start + this.pageSize);
  });
  readonly pageEnd = computed(() =>
    Math.min((this.page() + 1) * this.pageSize, this.sortedAccounts().length),
  );
  readonly pageStart = computed(() =>
    this.sortedAccounts().length ? this.page() * this.pageSize + 1 : 0,
  );

  constructor() {
    effect(() => {
      const guid = this.store.activeGuid();
      untracked(() => {
        this.page.set(0);
        if (this._reportRef && this._reportGuid !== guid) {
          this._closeReportDialog();
        }
      });
    });
    this._destroyRef.onDestroy(() => {
      this._closeReportDialog();
    });
  }

  async openReport(): Promise<void> {
    const guid = this.store.activeGuid();
    const report = await this.store.loadReport();
    if (!guid || !report || this.store.activeGuid() !== guid) return;

    this._closeReportDialog();
    const data: CreditReportDialogData = { report };
    const ref = this._dialog.open(CreditReportDialogComponent, {
      showHeader: false,
      width: 'min(72rem, 96vw)',
      modal: true,
      closable: false,
      dismissableMask: true,
      closeOnEscape: true,
      contentStyle: { overflow: 'hidden', padding: '0' },
      styleClass: 'bn-credit-report-dialog',
      data,
    });
    if (!ref) return;
    this._reportRef = ref;
    this._reportGuid = guid;
    ref.onClose.subscribe(() => {
      if (this._reportRef === ref) {
        this._reportRef = null;
        this._reportGuid = null;
      }
    });
  }

  async sendToClient(): Promise<void> {
    const guid = this.store.activeGuid();
    if (!guid) return;
    const sent = await this.sendAction.send(guid);
    if (sent && this.store.activeGuid() === guid) {
      this.store.startPostInvitePolling(guid);
    }
  }

  previousPage(): void {
    this.page.update((page) => Math.max(0, page - 1));
  }

  nextPage(): void {
    this.page.update((page) => Math.min(this.pageCount() - 1, page + 1));
  }

  onTableSort(event: { field?: string | string[]; order?: number }): void {
    const field = event.field;
    if (
      typeof field !== 'string' ||
      !LIABILITY_SORT_COLUMNS.includes(field as LiabilitySortColumn)
    ) {
      return;
    }
    const direction = event.order === -1 ? 'desc' : 'asc';
    if (field === this.sortColumn() && direction === this.sortDirection()) {
      return;
    }
    this.sortColumn.set(field as LiabilitySortColumn);
    this.sortDirection.set(direction);
    this.page.set(0);
  }

  formatOriginDate(date: string): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${date}T00:00:00Z`));
  }

  private _formatMonthYear(date: string | undefined): string {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    }).format(new Date(`${date}T00:00:00Z`));
  }

  private _parsePercentage(value: string): number {
    const percentage = Number(value);
    return Number.isFinite(percentage)
      ? Math.max(0, Math.min(100, percentage))
      : 0;
  }

  private _scoreDomain(scores: number[]): { minimum: number; maximum: number } {
    const minimumScore = Math.min(...scores);
    const maximumScore = Math.max(...scores);
    const padding = Math.max(
      10,
      Math.ceil((maximumScore - minimumScore) * 0.15),
    );
    let minimum = Math.floor((minimumScore - padding) / 10) * 10;
    let maximum = Math.ceil((maximumScore + padding) / 10) * 10;
    if (minimum === maximum) {
      minimum -= 10;
      maximum += 10;
    }
    return { minimum, maximum };
  }

  private _closeReportDialog(): void {
    const ref = this._reportRef;
    if (!ref) return;
    this._reportRef = null;
    this._reportGuid = null;
    ref.close();
  }
}
